import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  refusalOf,
  refusalSentence,
  type Refusal,
  type RefusalFix,
  type RefusalSentence,
} from './refusals';

/**
 * A refusal, turned into words — the only place `refusalSentence`'s keys meet
 * `t()`.
 *
 * It exists so that "render your own sentence, never the server's" is one habit
 * with one implementation rather than a discipline every call site has to keep.
 * A toast, an inline alert and a form banner all ask this for the same three
 * strings, so a code that grows a better sentence grows it everywhere at once —
 * and a call site cannot quietly go back to `error.response.data.message`
 * without deleting an import that says why it must not.
 */

/** What a caller renders: three strings and where the button leads. */
export interface RefusalText {
  title: string;
  body: string;
  /** The button's label, or `null` where the fix is the form already on screen. */
  fixLabel: string | null;
  fix: RefusalFix;
  /** Developer-only — for a `data-` attribute or a bug report. Never rendered. */
  serverMessage: string;
}

/** The names a sentence may need, beyond what the refusal itself carries. */
export interface RefusalSubject {
  /** The resource the operator is standing at, where there is one. */
  resource?: string;
  /** What was being saved — the draft rule's name, for the ambiguity sentence. */
  subject?: string;
}

export function useRefusalText() {
  const { t } = useTranslation();

  return useCallback(
    (refusal: Refusal, names: RefusalSubject = {}): RefusalText => {
      const sentence: RefusalSentence = refusalSentence(refusal, {
        // Falling back to a localized noun rather than to an id or an empty
        // string: "Nothing prices at this hour" reads as a bug, and
        // "Nothing prices cmt64plm… at this hour" is the bug this file exists
        // to prevent.
        resource: names.resource || t('spaces.pricingRules.lists.thisResource'),
        list: refusal.pricingListName || t('spaces.pricingRules.lists.aList'),
        subject: names.subject,
        otherRule: t('spaces.pricingRules.anotherRule'),
      });

      return {
        title: t(sentence.titleKey, sentence.titleParams),
        body: t(sentence.bodyKey, sentence.bodyParams),
        fixLabel: sentence.fixKey ? t(sentence.fixKey) : null,
        fix: sentence.fix,
        serverMessage: refusal.serverMessage,
      };
    },
    [t],
  );
}

/**
 * A refusal that has nowhere on screen to live, said in the venue's language.
 *
 * The failure being prevented is the one that shipped: `toast.error(apiMessage(
 * error, …))` puts whatever the API wrote in front of whoever pressed the
 * button, in English, and a toast has already faded by the time anyone could
 * ask what it meant. Two lines — what happened and what to do — out of the
 * locale files instead.
 */
export function useRefusalToast() {
  const text = useRefusalText();

  return useCallback(
    (error: unknown, names: RefusalSubject = {}) => {
      const rendered = text(refusalOf(error), names);
      toast.error(rendered.title, { description: rendered.body });
    },
    [text],
  );
}
